import { forwardRef } from 'react';

import { cx } from '../../lib/cx';
import { useControlId } from '../../lib/use-id';
import { Field, fieldIds } from '../field';
import type { SelectProps } from './types';

/**
 * A native select: keyboard, screen readers and mobile pickers work for free; we only style it.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, placeholder, help, error, id: providedId, className, required, ...props },
  ref,
) {
  const id = useControlId(providedId);
  const ids = fieldIds(id);
  return (
    <Field
      id={id}
      label={label}
      {...(help ? { help } : {})}
      {...(error ? { error } : {})}
      {...(required ? { required } : {})}
    >
      <select
        ref={ref}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? ids.error : help ? ids.help : undefined}
        required={required}
        className={cx(
          'control-touch rounded-control border bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors',
          'focus:border-brand-500 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-50 disabled:text-gray-500',
          error ? 'border-danger-600' : 'border-gray-300',
          className,
        )}
        {...props}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
});
